/**
 * NewBudgetDialog — crear o editar un presupuesto.
 *
 * Extraído de BudgetsPage (2026-08-26): la ficha del paciente también
 * necesita poder editar un presupuesto sin ir a la pestaña Presupuestos.
 * En modo edición (`budget` presente) el selector de paciente y el
 * "pick" de la agenda del día se ocultan — solo se cambian cirugía,
 * conceptos e importes.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api, formatEUR, formatDate } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from './ui/command';
import { Plus, Trash2, Loader2, CalendarDays, UserPlus, Check, Sparkles, ChevronsUpDown } from 'lucide-react';
import { toast } from 'sonner';
import { PatientCombobox } from './PatientCombobox';
import { QuickNewPatientDialog } from './QuickNewPatientDialog';

// ----- Helpers extracted to keep NewBudgetDialog focused on its own state -----
const _normSearch = (s) => (s || '').toString().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

function AgendaQuickPickPanel({ todayAgenda, todayLoading, selectedPatientId, visibleAppointments, onPickAppointment, onNewPatient }) {
  const [open, setOpen] = useState(false);
  const titleSuffix = todayAgenda?.agenda_date ? ` · ${todayAgenda.agenda_date}` : '';
  const title = todayAgenda?.is_today ? 'Pacientes de la agenda de hoy' : `Última agenda disponible${titleSuffix}`;
  const totalCount = visibleAppointments.length;

  // Find which appointment is currently selected (if any) so we can highlight it.
  const selectedAppointment = visibleAppointments.find((a) => a.patient_id && a.patient_id === selectedPatientId);
  const triggerLabel = selectedAppointment
    ? `${selectedAppointment.start_time} · ${selectedAppointment.patient_name}`
    : totalCount > 0
      ? `Elegir paciente de la agenda (${totalCount} citas)`
      : 'Sin pacientes en la agenda';

  return (
    <div className="rounded-lg border border-[rgba(46,110,94,0.35)] bg-[rgba(46,110,94,0.06)] p-3" data-testid="budget-agenda-quickpick">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 text-sm font-semibold text-[#13231E]">
          <CalendarDays className="w-4 h-4" />
          {title}
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onNewPatient}
          className="text-[#13231E] hover:bg-white"
          data-testid="budget-new-patient-button"
        >
          <UserPlus className="w-4 h-4 mr-1" /> Nuevo paciente
        </Button>
      </div>
      {todayLoading ? (
        <div className="text-xs text-muted-foreground flex items-center gap-2">
          <Loader2 className="w-3 h-3 animate-spin" /> Cargando agenda…
        </div>
      ) : totalCount === 0 ? (
        <div className="text-xs text-muted-foreground">No hay citas en la agenda del día.</div>
      ) : (
        <>
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                role="combobox"
                aria-expanded={open}
                data-testid="budget-agenda-trigger"
                className="w-full justify-between font-normal bg-white"
              >
                <span className={`truncate ${!selectedAppointment ? 'text-muted-foreground' : 'text-[#13231E] font-medium'}`}>
                  {triggerLabel}
                </span>
                <ChevronsUpDown className="w-4 h-4 ml-2 opacity-50 shrink-0" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
              <Command filter={(v, s) => (_normSearch(v).includes(_normSearch(s)) ? 1 : 0)}>
                <CommandInput placeholder="Buscar por hora, nombre o tipo de visita…" data-testid="budget-agenda-search" />
                <CommandList>
                  <CommandEmpty>Sin coincidencias.</CommandEmpty>
                  <CommandGroup>
                    {visibleAppointments.map((a) => {
                      const isSelected = !!a.patient_id && a.patient_id === selectedPatientId;
                      const needsCreate = !a.patient_id;
                      const searchString = `${a.start_time} ${a.patient_name} ${a.visit_type || ''}`;
                      return (
                        <CommandItem
                          key={`${a.start_time}-${a.patient_name}`}
                          value={searchString}
                          onSelect={() => { onPickAppointment(a); setOpen(false); }}
                          data-testid="budget-agenda-item"
                        >
                          <span className="font-mono text-[11px] text-muted-foreground w-12 shrink-0">{a.start_time}</span>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium truncate">{a.patient_name}</div>
                            {a.visit_type && (
                              <div className="text-[11px] text-muted-foreground truncate">{a.visit_type}</div>
                            )}
                          </div>
                          {isSelected
                            ? <Check className="w-4 h-4 ml-2 text-[#13231E] shrink-0" />
                            : needsCreate
                              ? <Sparkles className="w-3.5 h-3.5 ml-2 opacity-60 shrink-0" />
                              : null}
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          <div className="text-[11px] text-muted-foreground mt-1.5 flex items-center gap-3 flex-wrap">
            <span className="flex items-center gap-1"><Check className="w-3 h-3" /> Ya en BBDD</span>
            <span className="flex items-center gap-1"><Sparkles className="w-3 h-3" /> Nuevo (al pulsar lo crearás)</span>
          </div>
        </>
      )}
    </div>
  );
}

function NewBudgetDialog({ open, onOpenChange, onCreated, initialPatient, patients, onPatientsRefresh, budget }) {
  // `budget` set → modo edición: se cambia la cirugía y los importes de uno ya creado.
  const editing = !!budget;
  const [surgeries, setSurgeries] = useState([]);
  const [template_code, setTemplateCode] = useState('');
  const [patient_id, setPatientId] = useState(initialPatient || '');
  // Each item gets a stable local _uid used only for React keys; stripped before POST.
  const newItem = (concept = '', amount = 0) => ({ _uid: `it-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, concept, amount });
  const [items, setItems] = useState([newItem()]);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  // Today's agenda quick-pick state
  const [todayAgenda, setTodayAgenda] = useState(null); // { agenda_id, agenda_date, is_today, appointments }
  const [todayLoading, setTodayLoading] = useState(false);

  // Quick "new patient" sub-dialog state
  const [quickNewOpen, setQuickNewOpen] = useState(false);
  const [quickNewSeed, setQuickNewSeed] = useState('');

  useEffect(() => {
    if (!open) return;
    api.get('/api/surgeries').then(r => setSurgeries(r.data));
    if (editing) {
      // Precargamos el presupuesto existente; el paciente ya no se cambia.
      setPatientId(budget.patient_id || '');
      setTemplateCode(budget.surgery_code || '');
      setItems((budget.items || []).length
        ? budget.items.map(it => newItem(it.concept, Number(it.amount || 0)))
        : [newItem()]);
      setNotes(budget.notes || '');
      return;
    }
    setPatientId(initialPatient || '');
    setTemplateCode('');
    setItems([newItem()]);
    setNotes('');
    // (2026-08-29: retirado el flujo de agendas PDF legacy; los chips de
    // "agenda de hoy" venían de ahí y llevaban meses saliendo vacíos.)
    setTodayAgenda(null);
    setTodayLoading(false);
  }, [open, initialPatient, editing, budget]);

  const onPickTemplate = (code) => {
    setTemplateCode(code);
    const s = surgeries.find(x => x.code === code);
    if (!s) return;
    setItems(currentItems => {
      // Editando: cambiar de cirugía sustituye la primera línea (la cirugía),
      // conservando las líneas extra que se hubieran añadido a mano.
      if (editing && currentItems.length) {
        return currentItems.map((it, i) => i === 0 ? { ...it, concept: s.name, amount: Number(s.price || 0) } : it);
      }
      // Replace first empty row or append
      const hasEmpty = currentItems.length === 1 && !currentItems[0].concept;
      if (hasEmpty) return [newItem(s.name, Number(s.price || 0))];
      return [...currentItems, newItem(s.name, Number(s.price || 0))];
    });
  };

  const onPickAgendaAppointment = (appt) => {
    if (appt.patient_id) {
      // Matched against an existing patient → simply select.
      setPatientId(appt.patient_id);
      toast.success(`${appt.patient_name} seleccionado`);
    } else {
      // Not matched → open the quick-create dialog pre-filled with the name.
      setQuickNewSeed(appt.patient_name || '');
      setQuickNewOpen(true);
    }
  };

  const onQuickPatientCreated = (newPatient) => {
    // Make the new patient instantly selectable & selected.
    onPatientsRefresh?.();
    setPatientId(newPatient.id);
  };

  const updateItem = (i, k, v) => setItems(arr => arr.map((it, idx) => idx === i ? { ...it, [k]: k === 'amount' ? Number(v) : v } : it));
  const addItem = () => setItems(arr => [...arr, newItem()]);
  const removeItem = (i) => setItems(arr => arr.filter((_, idx) => idx !== i));
  const total = useMemo(() => items.reduce((s, it) => s + Number(it.amount || 0), 0), [items]);
  const submit = async (e) => {
    e.preventDefault();
    if (!patient_id) return toast.error('Selecciona un paciente');
    // Strip the internal _uid before sending to backend
    const validItems = items.filter(it => it.concept).map(({ _uid, ...rest }) => rest);
    if (!validItems.length) return toast.error('Añade al menos un concepto');
    setBusy(true);
    try {
      if (editing) {
        const res = await api.put(`/api/budgets/${budget.id}`, {
          items: validItems,
          surgery_code: template_code || null,
          notes,
        });
        toast.success(`Presupuesto ${res.data.number} actualizado · ${formatEUR(res.data.total)}`);
        onCreated && onCreated(res.data);
        onOpenChange(false);
        return;
      }
      const res = await api.post('/api/budgets', { patient_id, surgery_code: null, items: validItems, notes, valid_days: 60 });
      const auto = res.data.auto_generated || {};
      if (auto.preop_id && auto.instructions_id) {
        toast.success(`Presupuesto ${res.data.number} creado · Preoperatorio + Indicaciones generados automáticamente`);
      } else {
        toast.success(`Presupuesto ${res.data.number} creado`);
      }
      onCreated && onCreated(res.data);
      onOpenChange(false);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Error'); } finally { setBusy(false); }
  };

  // Show all appointments of the day. The chips container scrolls vertically
  // when there are too many to fit (typical for full-day clinic agendas).
  const visibleAppointments = todayAgenda?.appointments || [];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {editing ? `Editar presupuesto ${budget.number}` : 'Nuevo presupuesto'}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          {/* Today's agenda quick-pick — extracted to a dedicated component to
              keep this dialog under control. */}
          {!editing && (todayLoading || (todayAgenda && todayAgenda.appointments?.length > 0)) && (
            <AgendaQuickPickPanel
              todayAgenda={todayAgenda}
              todayLoading={todayLoading}
              selectedPatientId={patient_id}
              visibleAppointments={visibleAppointments}
              onPickAppointment={onPickAgendaAppointment}
              onNewPatient={() => { setQuickNewSeed(''); setQuickNewOpen(true); }}
            />
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label>Paciente*</Label>
              {editing ? (
                <div className="h-10 flex items-center px-3 rounded-md border border-border bg-muted text-sm font-medium">
                  {budget.patient_name}
                </div>
              ) : (
                <PatientCombobox value={patient_id} onChange={setPatientId} patients={patients} dataTestid="budget-patient-select" />
              )}
            </div>
            <div>
              <Label>{editing ? 'Cirugía / Procedimiento' : 'Plantilla / Procedimiento (opcional)'}</Label>
              <Select value={template_code} onValueChange={onPickTemplate}>
                <SelectTrigger data-testid="budget-template-select"><SelectValue placeholder="— elige un procedimiento habitual —" /></SelectTrigger>
                <SelectContent className="max-h-80">
                  {surgeries.map(s => (
                    <SelectItem key={s.code} value={s.code}>
                      {s.name}{s.price > 0 ? ` · ${formatEUR(s.price)}` : ' · (precio a definir)'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <Label>Conceptos e importes</Label>
          <div className="space-y-2">
            {items.map((it, i) => (
              <div key={it._uid} className="grid grid-cols-[1fr_140px_36px] gap-2">
                <Input value={it.concept} onChange={(e) => updateItem(i, 'concept', e.target.value)} placeholder="Concepto" />
                <Input type="number" step="0.01" value={it.amount} onChange={(e) => updateItem(i, 'amount', e.target.value)} placeholder="Importe €" />
                <Button type="button" variant="ghost" size="icon" onClick={() => removeItem(i)} className="text-destructive"><Trash2 className="w-4 h-4" /></Button>
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" onClick={addItem}><Plus className="w-4 h-4 mr-1" /> Añadir concepto</Button>
          </div>
          <div><Label>Notas (opcional)</Label><Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notas adicionales…" /></div>
          <div className="flex items-center justify-between pt-3 border-t border-border">
            <div className="text-sm text-muted-foreground">
              {editing ? `Vence: ${formatDate(budget.valid_until)}` : 'Validez: 60 días'}
            </div>
            <div className="text-xl font-display font-semibold text-[#13231E]">Total: {formatEUR(total)}</div>
          </div>
          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" disabled={busy} className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid={editing ? 'budget-save-button' : 'budget-create-button'}>
              {busy
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {editing ? 'Guardando…' : 'Creando…'}</>
                : (editing ? 'Guardar cambios' : 'Crear presupuesto')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
      <QuickNewPatientDialog
        open={quickNewOpen}
        onOpenChange={setQuickNewOpen}
        initialName={quickNewSeed}
        onCreated={onQuickPatientCreated}
      />
    </Dialog>
  );
}


export default NewBudgetDialog;
